// @flow

import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  TableCell,
  TableRow,
  Typography,
  CurrencyInput,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  Table,
  TableBody,
  TableHead,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { TableContainer } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import useIntl from '../../../../hooks/useIntl';
import type { Step1TableProps } from '../types';
import messages from '../messages';
import { validatePositiveAmount } from '../utils';
import {
  HEADER_MESSAGE_KEYS,
  bodyCellSx,
  compactCurrencyInputSx,
  currencyInputErrorHelperTextProps,
  floorPlanCellSx,
  headCellSx,
  inputCellSx,
  inputHeadCellSx,
  tableContainerSx,
  tableSx,
  transparentRowSx,
} from './constants';
import { FloorPlanName, formatCurrencyCell } from './hooks';

const Step1Table = ({
  reactHookFormProps,
  disabled,
  floorPlans,
}: Step1TableProps) => {
  const intl = useIntl();
  const { control, Controller, trigger } = reactHookFormProps;
  const positiveAmountErrorMessage = intl.formatMessage(
    messages.mustBeGreaterThanZero,
  );

  const handleAmountChange = (field) => (event) => {
    field.onChange(event);
    trigger(field.name).catch(() => undefined);
  };

  return (
    <TableContainer sx={tableContainerSx}>
      <Table size="small" sx={tableSx}>
        <TableHead sx={{ backgroundColor: 'transparent' }}>
          <TableRow sx={transparentRowSx}>
            {HEADER_MESSAGE_KEYS.map((messageKey) => (
              <TableCell
                key={messageKey}
                variant="head"
                align="left"
                sx={
                  messageKey === 'newGrossRent' ||
                  messageKey === 'newUtilityAllowance'
                    ? inputHeadCellSx
                    : headCellSx
                }
              >
                <Typography variant="labelLarge" component="span">
                  <FormattedMessage {...messages[messageKey]} />
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {floorPlans.map((floorPlan) => (
            <TableRow key={floorPlan.floorPlanId} sx={transparentRowSx}>
              <TableCell variant="body" align="left" sx={floorPlanCellSx}>
                <FloorPlanName name={floorPlan.floorPlanName} />
              </TableCell>
              <TableCell variant="body" align="left" sx={bodyCellSx}>
                <Typography variant="bodySmall" component="span">
                  {floorPlan.bedsBath}
                </Typography>
              </TableCell>
              <TableCell variant="body" align="left" sx={bodyCellSx}>
                <Typography variant="bodySmall" component="span">
                  {formatCurrencyCell(floorPlan.currentGrossRent)}
                </Typography>
              </TableCell>
              <TableCell variant="body" align="left" sx={inputCellSx}>
                <Controller
                  name={`floorPlanChanges.${floorPlan.floorPlanId}.newGrossRent`}
                  control={control}
                  rules={{
                    validate: (value) =>
                      validatePositiveAmount(value, positiveAmountErrorMessage),
                  }}
                  render={({ field, fieldState }) => {
                    const hasError = Boolean(fieldState.error?.message);

                    return (
                      <CurrencyInput
                        {...field}
                        value={field.value ?? ''}
                        onChange={handleAmountChange(field)}
                        disabled={disabled}
                        fullWidth={false}
                        error={hasError}
                        helperText={
                          hasError ? fieldState.error?.message : undefined
                        }
                        showHelperText={hasError}
                        FormHelperTextProps={currencyInputErrorHelperTextProps}
                        min={0}
                        step={0.01}
                        sx={compactCurrencyInputSx}
                      />
                    );
                  }}
                />
              </TableCell>
              <TableCell variant="body" align="left" sx={bodyCellSx}>
                <Typography variant="bodySmall" component="span">
                  {formatCurrencyCell(floorPlan.currentUtilityAllowance)}
                </Typography>
              </TableCell>
              <TableCell variant="body" align="left" sx={inputCellSx}>
                <Controller
                  name={`floorPlanChanges.${floorPlan.floorPlanId}.newUtilityAllowance`}
                  control={control}
                  rules={{
                    validate: (value) =>
                      validatePositiveAmount(value, positiveAmountErrorMessage),
                  }}
                  render={({ field, fieldState }) => {
                    const hasError = Boolean(fieldState.error?.message);

                    return (
                      <CurrencyInput
                        {...field}
                        value={field.value ?? ''}
                        onChange={handleAmountChange(field)}
                        disabled={disabled}
                        fullWidth={false}
                        error={hasError}
                        helperText={
                          hasError ? fieldState.error?.message : undefined
                        }
                        showHelperText={hasError}
                        FormHelperTextProps={currencyInputErrorHelperTextProps}
                        min={0}
                        step={0.01}
                        sx={compactCurrencyInputSx}
                      />
                    );
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default Step1Table;
